import Auth from "@aws-amplify/auth"

const RETURN_ROUTE_KEY = "badger-hosted-ui-return-route"
const RETURN_ROUTE_TTL_MS = 30 * 60 * 1000

type BrowserLocation = Pick<Location, "origin" | "pathname" | "search" | "hash">

type ReturnRouteRecord = {
  target: string
  capturedAt: number
}

const isExcludedPath = (pathname: string) =>
  /^\/(callback|logout)(?:\/|$)/i.test(pathname)

export const validatedLocalReturnRoute = (
  candidate: unknown,
  origin: string
): string | null => {
  if (
    typeof candidate !== "string" ||
    !candidate.startsWith("/") ||
    candidate.startsWith("//") ||
    candidate.includes("\\")
  ) {
    return null
  }

  try {
    const url = new URL(candidate, origin)
    if (
      url.origin !== origin ||
      !["http:", "https:"].includes(url.protocol) ||
      isExcludedPath(url.pathname)
    ) {
      return null
    }
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export const captureHostedUIReturnRoute = (
  location: BrowserLocation = window.location,
  storage: Pick<Storage, "setItem" | "removeItem"> = window.sessionStorage,
  now: number = Date.now()
) => {
  if (/^\/logout(?:\/|$)/i.test(location.pathname)) {
    clearHostedUIReturnRoute(storage)
    return
  }
  // A callback retry must retain the route captured before leaving Titan.
  if (/^\/callback(?:\/|$)/i.test(location.pathname)) return

  const target = validatedLocalReturnRoute(
    `${location.pathname}${location.search}${location.hash}`,
    location.origin
  )
  if (!target) {
    clearHostedUIReturnRoute(storage)
    return
  }

  try {
    storage.setItem(
      RETURN_ROUTE_KEY,
      JSON.stringify({ target, capturedAt: now } as ReturnRouteRecord)
    )
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. Login
    // must still proceed; callback navigation will safely fall back to root.
  }
}

export const consumeHostedUIReturnRoute = (
  location: Pick<Location, "origin"> = window.location,
  storage: Pick<Storage, "getItem" | "removeItem"> = window.sessionStorage,
  now: number = Date.now()
): string => {
  let raw: string | null = null
  try {
    raw = storage.getItem(RETURN_ROUTE_KEY)
    storage.removeItem(RETURN_ROUTE_KEY)
  } catch {
    return "/"
  }
  if (!raw) return "/"

  try {
    const record = JSON.parse(raw) as Partial<ReturnRouteRecord>
    if (
      typeof record.capturedAt !== "number" ||
      !Number.isFinite(record.capturedAt) ||
      record.capturedAt > now ||
      now - record.capturedAt > RETURN_ROUTE_TTL_MS
    ) {
      return "/"
    }
    return validatedLocalReturnRoute(record.target, location.origin) ?? "/"
  } catch {
    return "/"
  }
}

export const clearHostedUIReturnRoute = (
  storage: Pick<Storage, "removeItem"> = window.sessionStorage
) => {
  try {
    storage.removeItem(RETURN_ROUTE_KEY)
  } catch {
    // Clearing a navigation hint must never block logout.
  }
}

export const beginHostedUISignIn = () => {
  captureHostedUIReturnRoute()
  return Auth.federatedSignIn()
}
